/// <amd-module name="Build/Shared/Components/Histogram/Histogram" />
import * as React from "react";
import { FPSLinkComponent } from "VSS/Platform/Components/FPSLink";
import { FocusZone, FocusZoneContext, FocusZoneDirection, IFocusZoneContext } from "VSSUI/FocusZone";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IHistogramItem, IHistogramProps, IHistogramState } from "./Histogram.types";

export namespace HistogramStates {
    export const Success = "succeeded";
    export const Failure = "failure";
    export const Warning = "warning";
    export const Running = "running";
    export const Unknown = "unknown";
}

interface IRange {
    min: number;
    max: number;
}

export const TelemetryContext = React.createContext("build-histogram");

const c_HistogramWidth = 22;
const c_HistogramHeight = 80;

namespace Helper {
    const percentageRange: IRange = {
        max: 90,
        min: 0
    };

    export function getOffsetValueInPixels(value: number | undefined, valueRange: IRange, height: number): number {
        const max = valueRange.max || 100;
        value = value || 100;
        let newValue = (100 / max) * value;
        newValue = getCorrectedValue(newValue);
        // Note: We are not using percentages here since we get paddingTop, which is ALWAYS relative to the width of the container
        // we need it to be relative to the height, but, with out absolute positioning it isn't possible, and there's no reason to use absolute positioning with what we have
        newValue = (height / 100) * newValue;
        return newValue;
    }

    export function getRange(values: number[]): IRange {
        return {
            min: Math.min(...values),
            max: Math.max(...values)
        };
    }

    function getCorrectedValue(value: number) {
        // 100% implies we will be emptying out the whole item, max allowed is 90
        value = 100 - value;
        value = value > percentageRange.max ? percentageRange.max : value;
        value = value < percentageRange.min ? percentageRange.min : value;
        return value;
    }
}

export class Histogram extends React.Component<IHistogramProps, IHistogramState> {
    private static s_noHoverStylesClassName = "no-hover-style";

    constructor(props: IHistogramProps) {
        super(props);
        this.state = { hoveredItem: null };
    }

    public render() {
        if (this.props.items.length == 0) {
            return null;
        } else {
            let items = this.props.items.slice() || [];
            const width = this.props.width || c_HistogramWidth;
            const height = this.props.height || c_HistogramHeight;

            if (this.props.minimumLength && items.length < this.props.minimumLength) {
                const placeholders: IHistogramItem[] = [];
                for (let i = 0; i < this.props.minimumLength - items.length; ++i) {
                    const dummyItem: IHistogramItem = {
                        id: "_histogram_placeholder_" + i,
                        value: 0,
                        noHoverStyles: true,
                        state: s_placeholderState
                    };
                    placeholders.push(dummyItem);
                }

                items = placeholders.concat(items);
            }

            const rangeValues = items.map(x => x.value || 0);
            const range = Helper.getRange(rangeValues);

            let content = (
                <ul className="histogram-items-list flex flex-row" aria-label={this.props.ariaLabelForList}>
                    {items.map((item, index) => {
                        let listItemOpacity = 1;
                        if (this.props.selectRightMost) {
                            if (index != items.length - 1) {
                                // not right most, dim down the original opacity
                                listItemOpacity = 0.5;
                            }
                        }

                        // if the item isn't excluded from hover styles, and the currently hovered item isn't a placeholder...
                        if (this.state.hoveredItem !== null && !item.noHoverStyles && items[this.state.hoveredItem].state !== s_placeholderState) {
                            if (index !== this.state.hoveredItem) {
                                listItemOpacity = 0.5;
                            } else {
                                listItemOpacity = 1;
                            }
                        }

                        const offset = Helper.getOffsetValueInPixels(item.value, range, height);
                        return (
                            <li
                                aria-hidden={item.noHoverStyles}
                                key={item.id}
                                className={css(
                                    "histogram-items-list-item",
                                    "padded-item relative",
                                    !!item.noHoverStyles && Histogram.s_noHoverStylesClassName
                                )}
                                onMouseOver={() => this.setState({ hoveredItem: index })}
                                onMouseOut={() => this.setState({ hoveredItem: null })}
                            >
                                <HistogramItem
                                    item={item}
                                    excludeTabStop={this.props.excludeTabStopOnItems}
                                    renderTooltip={
                                        item.state !== s_placeholderState && this.props.renderTooltip
                                            ? () => this.props.renderTooltip!(item)
                                            : undefined
                                    }
                                    styles={{
                                        paddingTop: item.state === s_placeholderState ? "0px" : offset + "px",
                                        width: width,
                                        height: height - (item.state === s_placeholderState ? 0 : offset),
                                        opacity: listItemOpacity
                                    }}
                                />
                            </li>
                        );
                    })}
                </ul>
            );

            if (!this.props.noFocusZone) {
                content = (
                    <FocusZone direction={FocusZoneDirection.Horizontal} circularNavigation={true} {...this.props.focusZoneProps}>
                        {content}
                    </FocusZone>
                );
            }

            return (
                <div className={css("build-common-histogram", this.props.className)}>
                    {content}
                    {!this.props.noBorderElement && <div className="border-element"></div>}
                </div>
            );
        }
    }
}

interface IHistogramItemProps {
    item: IHistogramItem;
    excludeTabStop?: boolean;
    className?: string;
    isSelected?: boolean;
    renderTooltip?: () => React.ReactNode;
    styles: {
        paddingTop: string;
        width: number;
        height: number;
        opacity: number;
    };
}

class HistogramItem extends React.PureComponent<IHistogramItemProps> {
    private static s_itemClassName = "histogram-item";

    public render() {
        const item = this.props.item;
        const containerStyle = {
            paddingTop: this.props.styles.paddingTop
        };
        return (
            <Tooltip renderContent={this.props.renderTooltip} text={this.props.renderTooltip ? undefined : item.tooltip}>
                <span>
                    <div className="histogram-item-container" style={containerStyle}>
                        {item.href && (
                            <FocusZoneContext.Consumer>
                                {(zoneContext: IFocusZoneContext) => (
                                    <TelemetryContext.Consumer>
                                        {telemetrySource => (
                                            <FPSLinkComponent
                                                tabIndex={this.props.excludeTabStop ? -1 : 0}
                                                aria-label={item.tooltip}
                                                href={item.href}
                                                className={css(HistogramItem.s_itemClassName, ...this._getClassNameInputs(), this.props.className)}
                                                data-is-focusable={true}
                                                data-focuszone={zoneContext.focuszoneId}
                                                style={this._getStyles()}
                                                telemetrySource={telemetrySource}
                                            ></FPSLinkComponent>
                                        )}
                                    </TelemetryContext.Consumer>
                                )}
                            </FocusZoneContext.Consumer>
                        )}
                        {!item.href && item.state !== s_placeholderState && (
                            <div
                                className={css(HistogramItem.s_itemClassName, ...this._getClassNameInputs(), this.props.className)}
                                data-is-focusable={true}
                                style={this._getStyles()}
                            ></div>
                        )}
                        {!item.href && item.state === s_placeholderState && (
                            <div
                                className={css(HistogramItem.s_itemClassName, ...this._getClassNameInputs(), this.props.className)}
                                data-is-focusable={true}
                                style={this._getStyles()}
                                onClick={this._onPlaceholderClick}
                            ></div>
                        )}
                    </div>
                </span>
            </Tooltip>
        );
    }

    private _getClassNameInputs(): (string | false | null | undefined)[] {
        const itemState = this.props.item.state || HistogramStates.Unknown;
        return [
            itemState === HistogramStates.Success && "histogram-success-color",
            itemState === HistogramStates.Failure && "histogram-failure-color",
            itemState === HistogramStates.Warning && "histogram-warning-color",
            itemState === HistogramStates.Running && "histogram-running-color",
            itemState === HistogramStates.Unknown && "histogram-unknown-color",
            itemState === s_placeholderState && "histogram-placeholder-color",
            !!this.props.isSelected && "histogram-selected-item"
        ];
    }

    private _getStyles(): React.CSSProperties {
        const styles = {} as React.CSSProperties;

        const itemStyles = this.props.styles;
        if (itemStyles) {
            styles.width = itemStyles.width;
            styles.height = itemStyles.height;
            styles.opacity = itemStyles.opacity;
        }

        return styles;
    }

    private _onPlaceholderClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // just eat clicks on placeholder elements. otherwise it's weird when the histogram is embedded in a row that is itself a click target:
        // when you click on a non-placeholder element, you go to the build, and when you click on a placeholder, you go to a different build
        e.preventDefault();
    };
}

const s_placeholderState = "placeholder";
